import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import dayjs, { Dayjs } from "dayjs";

// 5-minute interval options
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const HOUR_OPTIONS   = ["1","2","3","4","5","6","7","8","9","10","11","12"];

interface Props {
  value: Dayjs | null;
  onChange: (val: Dayjs | null) => void;
  minDateTime?: Dayjs;
  placeholder?: string;
  inputSx?: React.CSSProperties | object;
  size?: "small" | "medium";
  fullWidth?: boolean;
}

// Pull h12, minute, ampm out of a Dayjs
const decompose = (v: Dayjs | null) => {
  if (!v || !v.isValid()) return { h12: 12, min: 0, ampm: "AM" as "AM" | "PM" };
  const h24  = v.hour();
  const h12  = h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
  return { h12, min: v.minute(), ampm };
};

// Build a Dayjs from date parts + time parts
const compose = (base: Dayjs, h12: number, min: number, ampm: "AM" | "PM"): Dayjs => {
  let h24 = h12 % 12;
  if (ampm === "PM") h24 += 12;
  return base.hour(h24).minute(min).second(0).millisecond(0);
};

const DateTimeDropdownPicker: React.FC<Props> = ({
  value,
  onChange,
  minDateTime,
  placeholder = "Date & Time",
  inputSx,
  size = "small",
  fullWidth = true,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Local time state — decoupled from value so edits don't clobber date
  const init   = decompose(value);
  const [hour,  setHour]  = useState(String(init.h12));
  const [minInput, setMinInput] = useState(String(init.min).padStart(2, "0"));
  const [ampm,  setAmpm]  = useState<"AM" | "PM">(init.ampm);

  // Keep local time in sync when value changes from outside
  useEffect(() => {
    const d = decompose(value);
    setHour(String(d.h12));
    setMinInput(String(d.min).padStart(2, "0"));
    setAmpm(d.ampm);
  }, [value]);

  // Called whenever any time part changes
  const applyTime = (h: string, m: string, ap: "AM" | "PM") => {
    const base = value && value.isValid() ? value : dayjs().startOf("day");
    const hNum = parseInt(h) || 12;
    const mNum = Math.min(59, Math.max(0, parseInt(m.replace(/\D/g, "")) || 0));
    onChange(compose(base, hNum, mNum, ap));
  };

  // Called when the calendar date changes
  const handleDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;
    const hNum = parseInt(hour) || 12;
    const mNum = Math.min(59, Math.max(0, parseInt(minInput) || 0));
    onChange(compose(newDate, hNum, mNum, ampm));
  };

  const displayValue = value && value.isValid()
    ? value.format("MM/DD/YYYY hh:mm A")
    : "";

  // Show a non-blocking error when value is before minDateTime.
  // The calendar has NO minDate restriction — user can still pick any date.
  // Clamping was intentionally removed so the picker never overrides user input.
  const isBelowMin = !!(
    value && value.isValid() &&
    minDateTime && minDateTime.isValid() &&
    value.isBefore(minDateTime)
  );

  return (
    <>
      {/* Trigger field */}
      <Box ref={anchorRef} sx={{ width: fullWidth ? "100%" : undefined }}>
        <TextField
          size={size}
          fullWidth={fullWidth}
          placeholder={placeholder}
          value={displayValue}
          onClick={() => setOpen(true)}
          error={isBelowMin}
          helperText={isBelowMin ? `Must be after ${minDateTime!.format("MMM D, YYYY h:mm A")}` : undefined}
          inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
          InputProps={{
            endAdornment: (
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: 18, color: isBelowMin ? "#dc2626" : "#94a3b8", cursor: "pointer", flexShrink: 0 }}
              />
            ),
            sx: inputSx,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: 13,
              bgcolor: isBelowMin ? "#fef2f2" : "#fff",
              cursor: "pointer",
            },
          }}
        />
      </Box>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top",    horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
            overflow: "hidden",
            width: 300,
          },
        }}
      >
        {/* ── Calendar ── */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={value}
            onChange={handleDateChange}
            sx={{
              width: "100%",
              "& .MuiPickersDay-root.Mui-selected": {
                bgcolor: "#2f3891",
                "&:hover": { bgcolor: "#2f3891" },
              },
              "& .MuiPickersDay-root:not(.Mui-selected):hover": { bgcolor: "#ede9fe" },
            }}
          />
        </LocalizationProvider>

        {/* ── Time row ── */}
        <Box
          sx={{
            px: 2, pb: 2, pt: 1.5,
            borderTop: "1px solid #f1f5f9",
            bgcolor: "#fafafe",
          }}
        >
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", mb: 1.25, textTransform: "uppercase", letterSpacing: 0.6 }}>
            Select Time
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

            {/* ── Hour dropdown ── */}
            <Autocomplete
              options={HOUR_OPTIONS}
              value={hour}
              onChange={(_, v) => {
                const h = v ?? "12";
                setHour(h);
                applyTime(h, minInput, ampm);
              }}
              disableClearable
              disablePortal
              sx={{ width: 72 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Hr"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 },
                    "& .MuiInputLabel-root": { fontSize: 11 },
                  }}
                />
              )}
              ListboxProps={{ sx: { fontSize: 13, maxHeight: 200 } }}
            />

            <Typography sx={{ fontWeight: 800, color: "#2f3891", fontSize: 18, mb: "2px" }}>:</Typography>

            {/* ── Minute dropdown ── */}
            <Autocomplete
              options={MINUTE_OPTIONS}
              value={minInput}
              onChange={(_, v) => {
                const m = v ?? "00";
                setMinInput(m);
                applyTime(hour, m, ampm);
              }}
              disableClearable
              disablePortal
              sx={{ width: 80 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Min"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 },
                    "& .MuiInputLabel-root": { fontSize: 11 },
                  }}
                />
              )}
              ListboxProps={{ sx: { fontSize: 13, maxHeight: 200 } }}
            />

            {/* ── AM / PM toggle ── */}
            <Box
              sx={{
                display: "flex",
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              {(["AM", "PM"] as const).map((period) => (
                <Button
                  key={period}
                  size="small"
                  disableElevation
                  onClick={() => {
                    setAmpm(period);
                    applyTime(hour, minInput, period);
                  }}
                  sx={{
                    minWidth: 42,
                    px: 0.75,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 0,
                    textTransform: "none",
                    bgcolor: ampm === period ? "#2f3891" : "#fff",
                    color:  ampm === period ? "#fff"    : "#64748b",
                    "&:hover": {
                      bgcolor: ampm === period ? "#2f3891" : "#f1f5f9",
                    },
                  }}
                >
                  {period}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateTimeDropdownPicker;
